import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  customerQueryOptions,
  customerUsageQueryOptions,
} from '../../lib/queries/customers';

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

function formatChartDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}`;
}

function buildChartData(
  dailyByUser: { date: string; userId: string; tokens: number }[],
  topUserId: string | undefined,
) {
  const dateMap = new Map<string, { topUser: number; rest: number }>();
  for (const row of dailyByUser) {
    const slot = dateMap.get(row.date) ?? { topUser: 0, rest: 0 };
    if (row.userId === topUserId) slot.topUser += row.tokens;
    else slot.rest += row.tokens;
    dateMap.set(row.date, slot);
  }
  return Array.from(dateMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({ date, ...v }));
}

export default function Usage({ id }: { id: string }) {
  const { data: tenant } = useQuery(customerQueryOptions(id));
  const { data: usageData } = useQuery(customerUsageQueryOptions(id));

  const topUser = usageData?.users[0];
  const chartData = buildChartData(usageData?.dailyByUser ?? [], topUser?.id);
  const remaining = tenant ? tenant.includedTokens - tenant.periodUsage : 0;
  const hasOverage = remaining < 0;

  return (
    <div className="space-y-4">
      {/* Chart card */}
      <div className="bg-bg-elevated rounded-lg border border-white/6 p-5">
        <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
          Token usage — Last 30 days
        </span>

        <div className="mt-4">
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart
              data={chartData}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id="gradTopUser" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#5e6ad2" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#5e6ad2" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradRest" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={formatChartDate}
                tick={{ fill: '#8e8ea8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis hide />
              <Tooltip
                contentStyle={{
                  background: '#141418',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#8e8ea8' }}
                labelFormatter={formatChartDate}
                formatter={(v: number, name: string) => [
                  formatTokens(v),
                  name === 'topUser'
                    ? (topUser?.hostUserId ?? 'Top user')
                    : 'Rest of team',
                ]}
              />
              <Area
                type="monotone"
                dataKey="topUser"
                stroke="#5e6ad2"
                strokeWidth={2}
                fill="url(#gradTopUser)"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="rest"
                stroke="#22c55e"
                strokeWidth={2}
                fill="url(#gradRest)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 flex gap-4">
          <div className="flex items-center gap-1.5">
            <span className="bg-accent h-2 w-2 rounded-full" />
            <span className="text-muted text-xs">
              {topUser?.hostUserId ?? 'Top user'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="bg-success h-2 w-2 rounded-full" />
            <span className="text-muted text-xs">Rest of team</span>
          </div>
        </div>
      </div>

      {/* Overage status card */}
      {tenant && (
        <div className="bg-bg-elevated rounded-lg border border-white/6 p-5">
          <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
            Overage status
          </span>
          <div
            className={[
              'mt-3 flex items-start gap-3 rounded-lg border p-4',
              hasOverage ? 'border-danger' : 'border-success',
            ].join(' ')}
          >
            {hasOverage ? (
              <AlertTriangle className="text-danger mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="text-success mt-0.5 h-4 w-4 shrink-0" />
            )}

            <div>
              <p
                className={[
                  'text-sm font-medium',
                  hasOverage ? 'text-danger' : 'text-success',
                ].join(' ')}
              >
                {hasOverage ? 'Overage active' : 'No overage'}
              </p>
              <p
                className={[
                  'mt-0.5 text-xs',
                  hasOverage ? 'text-danger' : 'text-success',
                ].join(' ')}
              >
                {hasOverage
                  ? `${formatTokens(Math.abs(remaining))} over allowance this period.`
                  : `${formatTokens(remaining)} tokens remaining. On track to stay within allowance.`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
